import { useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, ArrowLeft, CheckCircle2, Download, FileSpreadsheet, ImageIcon, Loader2, Package, RotateCcw, Upload, XCircle } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router'

import {
  type Analisis,
  enMarcha,
  esRespuestaDefinitiva,
  type Importacion,
  importacion,
  type Informe,
  type Mapeo,
  textoDelProgreso,
  urlDeLaPlantilla,
  useImportacion,
  useUltimaImportacion,
} from '../api/importacion'
import type { ErrorDeLaApi } from '../api/nucleo'
import { Aviso, Boton, Cargando, Pastilla, Selector, Tarjeta, Titulo, clases } from '../componentes/ui'
import { agruparAvisos, datoCorto, enCuantos, enMinuscula, type GrupoDeAvisos, primeros } from './avisosDeImportacion'
import { creditosEnLlano } from './consumo'
import { precioEnTexto, productosDeMuestra, textoPlano } from './vistaPreviaDeImportacion'

/**
 * Importar productos desde un Excel (docs/alta-de-web-con-plantillas.md §7.3): bajar la
 * plantilla, subir la hoja, **confirmar** qué columna va a qué campo, importar como
 * borradores y, si se quiere, publicar de golpe los que no tienen avisos. El mapeo que
 * propone el núcleo se enseña siempre antes de importar: un mapeo que nadie ve es un
 * catálogo mal importado que se nota cuando llega el primer pedido.
 *
 * Importar es un trabajo en cola: se pide, y esta pantalla sigue su progreso (el id va
 * en la dirección, `?trabajo=`) hasta enseñar el informe.
 */
/** De dónde sale cada asignación, en una pastilla corta: lo largo va en el `title`. */
const CONFIANZA: Record<Mapeo['confianza'], { texto: string; clave: 'neutra' | 'violeta' | 'construyendo' | 'publicado'; ayuda: string }> = {
  exacta: { texto: 'Plantilla', clave: 'publicado', ayuda: 'Coincide con nuestra plantilla.' },
  formato: { texto: 'WooCommerce', clave: 'publicado', ayuda: 'Columna de la exportación de WooCommerce reconocida.' },
  sinonimo: { texto: 'Por el nombre', clave: 'publicado', ayuda: 'Reconocida por el nombre de la columna.' },
  valores: { texto: 'Por los valores', clave: 'neutra', ayuda: 'Deducida por lo que hay escrito en la columna: compruébala.' },
  ia: { texto: 'IA · revísala', clave: 'violeta', ayuda: 'No casaba por el nombre y la IA propone a dónde va: confírmala.' },
  ninguna: { texto: 'Sin asignar', clave: 'construyendo', ayuda: 'Elige a qué campo va, o déjala sin importar.' },
}

/** Los pasos de una importación, como en WooCommerce: dónde estás y qué queda. */
const PASOS_DE_IMPORTACION = ['Subir el fichero', 'Columnas', 'Importar', 'Listo'] as const

export const PasosDeImportacion = ({ actual }: { actual: 0 | 1 | 2 | 3 }) => (
  <ol aria-label="Pasos de la importación" className="mb-5 flex items-center gap-2 overflow-x-auto text-[13px]">
    {PASOS_DE_IMPORTACION.map((paso, i) => (
      <li className="flex shrink-0 items-center gap-2" key={paso}>
        {i > 0 && <span aria-hidden className={clases('h-px w-6 sm:w-10', i <= actual ? 'bg-violeta' : 'bg-linea-fuerte')} />}
        <span
          aria-current={i === actual ? 'step' : undefined}
          className={clases(
            'inline-flex items-center gap-2 rounded-pastilla px-2.5 py-1 font-medium',
            i < actual && 'text-violeta',
            i === actual && 'bg-violeta text-white',
            i > actual && 'text-tenue',
          )}
        >
          <span
            aria-hidden
            className={clases(
              'grid size-5 place-items-center rounded-full text-[11px]',
              i < actual && 'bg-violeta-suave text-violeta',
              i === actual && 'bg-white/20',
              i > actual && 'border border-linea-fuerte',
            )}
          >
            {i < actual ? '✓' : i + 1}
          </span>
          {paso}
        </span>
      </li>
    ))}
  </ol>
)

/** Hasta tres valores de la columna, sin vacíos y sin HTML: es lo que el cliente reconoce de su hoja. */
const muestraDe = (analisis: Analisis, indice: number) =>
  analisis.muestra
    .map((fila) => textoPlano(fila[indice] ?? ''))
    .filter(Boolean)
    .slice(0, 3)

/** «Así quedarán»: los primeros productos como los va a crear el importador, con el mapeo de ahora. */
export const ComoQuedaran = ({ analisis, mapeo }: { analisis: Analisis; mapeo: Mapeo[] }) => {
  const productos = productosDeMuestra(analisis, mapeo)
  if (productos.length === 0) return null
  return (
    <section aria-label="Así quedarán tus productos" className="mb-5">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold">Así quedarán tus productos</h3>
        <span className="text-xs text-tenue">Los primeros {productos.length} de la hoja · se crean como borradores</span>
      </div>
      <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {productos.map((p, i) => (
          <li className="flex flex-col gap-2 rounded-caja border border-linea bg-papel p-3" key={i}>
            <div className="flex items-start gap-3">
              <span aria-hidden className="grid size-11 shrink-0 place-items-center rounded-caja bg-papel-2 text-tenue">
                <Package className="size-5" />
              </span>
              <div className="min-w-0">
                <p className="line-clamp-2 text-sm leading-snug font-medium" title={p.nombre}>
                  {p.nombre}
                </p>
                <p className="mt-0.5 text-sm">
                  {p.precio !== null ? (
                    <>
                      <span className="font-semibold">{precioEnTexto(p.precio)}</span>
                      {p.antes !== null && <span className="ml-2 text-xs text-tenue line-through">{precioEnTexto(p.antes)}</span>}
                    </>
                  ) : (
                    <span className="text-xs text-tenue">Sin precio: «Consultar»</span>
                  )}
                </p>
              </div>
            </div>
            {p.categorias.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {p.categorias.slice(0, 3).map((ruta) => (
                  <Pastilla clave="neutra" key={ruta.join('>')} punto={false}>
                    {ruta.join(' › ')}
                  </Pastilla>
                ))}
              </div>
            )}
            <p className="mt-auto flex flex-wrap gap-x-3 text-xs text-tenue">
              <span className="inline-flex items-center gap-1">
                <ImageIcon className="size-3.5" />
                {p.fotos === 0 ? 'Sin fotos' : p.fotos === 1 ? '1 foto' : `${p.fotos} fotos`}
              </span>
              {p.sku && <span>SKU {p.sku}</span>}
            </p>
          </li>
        ))}
      </ul>
    </section>
  )
}

export const SelectorDeArchivo = ({ alElegir, ocupado }: { alElegir: (f: File) => void; ocupado: boolean }) => {
  const [arrastrando, setArrastrando] = useState(false)
  return (
    <label
      className={clases(
        'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-caja border-2 border-dashed px-4 py-10 text-center transition-colors',
        arrastrando ? 'border-violeta bg-violeta-suave' : 'border-linea-fuerte bg-papel-2 hover:border-violeta',
        ocupado && 'pointer-events-none opacity-60',
      )}
      onDragLeave={() => setArrastrando(false)}
      onDragOver={(e) => {
        e.preventDefault()
        setArrastrando(true)
      }}
      onDrop={(e) => {
        e.preventDefault()
        setArrastrando(false)
        const f = e.dataTransfer.files?.[0]
        if (f) alElegir(f)
      }}
    >
      <FileSpreadsheet className="size-7 text-tenue" />
      <span className="text-sm font-medium">Arrastra tu Excel o pulsa para elegirlo</span>
      <span className="text-xs text-tenue">Un .xlsx o un .csv (también la exportación de WooCommerce) con una fila por producto o por variante. Hasta 10 MB y 2.000 filas.</span>
      <input
        accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
        className="sr-only"
        onChange={(e) => {
          const f = e.target.files?.[0]
          if (f) alElegir(f)
          e.target.value = ''
        }}
        type="file"
      />
    </label>
  )
}

/**
 * «Tu columna → se guarda en», como el mapeo de WooCommerce: una fila compacta por columna, con
 * su muestra debajo del nombre y el desplegable a lo ancho. Arriba, lo que se importa y lo que hay
 * que revisar; las columnas de un formato conocido que no hacen falta (las 40 de WooCommerce que
 * no son de un catálogo) van plegadas al final, para que no tapen las nueve que importan.
 */
export const TablaDeMapeo = ({ analisis, mapeo, cambiar }: { analisis: Analisis; cambiar: (m: Mapeo[]) => void; mapeo: Mapeo[] }) => {
  const usados = new Set(mapeo.map((m) => m.campo).filter((c): c is string => c !== null && c !== analisis.opcion))
  const hayNombre = mapeo.some((m) => m.campo === 'nombre')
  const seImportan = mapeo.filter((m) => m.campo !== null)
  const porRevisar = mapeo.filter((m) => m.confianza === 'ia' || (m.campo === null && m.confianza !== 'formato'))
  // Lo que el formato ya sabe que no hace falta: se puede abrir y cambiar, pero no estorba.
  const queNoHacenFalta = mapeo.filter((m) => m.campo === null && m.confianza === 'formato')
  const principales = mapeo.filter((m) => !queNoHacenFalta.includes(m))
  const etiquetaDe = (clave: null | string) =>
    clave === null ? null : clave === analisis.opcion ? 'Opción de variante' : (analisis.campos.find((c) => c.clave === clave)?.etiqueta ?? clave)

  const fila = (m: Mapeo) => {
    const muestra = muestraDe(analisis, m.indice)
    const origen = CONFIANZA[m.confianza]
    return (
      <li
        className={clases(
          'grid gap-x-6 gap-y-2 border-t border-linea px-4 py-3 first:border-t-0 sm:grid-cols-[minmax(0,1fr)_minmax(15rem,22rem)] sm:items-center',
          m.confianza === 'ia' && 'border-l-2 border-l-violeta bg-violeta-suave/30',
          m.campo === null && m.confianza === 'ninguna' && 'bg-papel-2',
        )}
        key={m.indice}
      >
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate text-sm font-medium" title={m.columna}>
              {m.columna || <span className="text-tenue">(sin cabecera)</span>}
            </span>
            <span title={m.motivo ? `${origen.ayuda} ${m.motivo}` : origen.ayuda}>
              <Pastilla clave={m.campo === null && m.confianza !== 'ninguna' ? 'neutra' : origen.clave} punto={false}>
                {m.campo === null && m.confianza === 'formato' ? 'No hace falta' : origen.texto}
              </Pastilla>
            </span>
          </div>
          <p className="mt-0.5 truncate text-xs text-tenue" title={muestra.join(' · ')}>
            {muestra.length > 0 ? (
              <>
                <span className="text-tinta-2">Muestra:</span> {muestra.join(' · ')}
              </>
            ) : (
              'Vacía en las primeras filas'
            )}
          </p>
          {m.confianza === 'ia' && m.motivo && <p className="mt-1 line-clamp-2 text-xs text-violeta">{m.motivo}</p>}
        </div>
        <Selector
          aria-label={`Campo para la columna ${m.columna}`}
          onChange={(e) => cambiar(mapeo.map((x) => (x.indice === m.indice ? { ...x, campo: e.target.value || null, confianza: e.target.value ? 'exacta' : 'ninguna' } : x)))}
          value={m.campo ?? ''}
        >
          <option value="">— No importar —</option>
          {analisis.campos.map((c) => (
            <option disabled={c.clave !== m.campo && usados.has(c.clave)} key={c.clave} value={c.clave}>
              {c.etiqueta}
              {c.obligatoria ? ' *' : ''}
            </option>
          ))}
          <option value={analisis.opcion}>Opción de variante (la cabecera es la opción: Talla, Color…)</option>
        </Selector>
      </li>
    )
  }

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2 text-[13px]" role="status">
        <Pastilla clave="publicado">
          {seImportan.length} {seImportan.length === 1 ? 'columna se importa' : 'columnas se importan'}
        </Pastilla>
        {porRevisar.length > 0 && <Pastilla clave="violeta">{porRevisar.length} por revisar</Pastilla>}
        {queNoHacenFalta.length > 0 && (
          <Pastilla clave="neutra" punto={false}>
            {queNoHacenFalta.length} no hacen falta
          </Pastilla>
        )}
        {seImportan.length > 0 && (
          <span className="text-tenue">
            {seImportan
              .map((m) => etiquetaDe(m.campo))
              .filter(Boolean)
              .join(' · ')}
          </span>
        )}
      </div>

      <div className="overflow-hidden rounded-caja border border-linea">
        <div className="hidden grid-cols-[minmax(0,1fr)_minmax(15rem,22rem)] gap-x-6 bg-papel-2 px-4 py-2 text-xs font-medium tracking-wide text-tenue uppercase sm:grid">
          <span>Columna de tu fichero</span>
          <span>Se guarda en</span>
        </div>
        <ul>{principales.map(fila)}</ul>
        {queNoHacenFalta.length > 0 && (
          <details className="border-t border-linea">
            <summary className="cursor-pointer px-4 py-3 text-[13px] text-tenue hover:text-tinta">
              {queNoHacenFalta.length} columnas de WooCommerce que un catálogo no necesita (ID, impuestos, envío, medidas…). Ábrelas si quieres importar alguna.
            </summary>
            <ul className="border-t border-linea">{queNoHacenFalta.map(fila)}</ul>
          </details>
        )}
      </div>

      {porRevisar.some((m) => m.confianza === 'ia') && (
        <p className="mt-3 text-[13px] text-tenue">
          Las marcadas «IA · revísala» no casaban por su nombre: la IA propone a dónde van. Nada se importa sin tu confirmación.
          {analisis.creditosPorIA ? ` Proponerlas ha gastado ${creditosEnLlano(analisis.creditosPorIA)}.` : ''}
        </p>
      )}
      {analisis.iaSinSaldo && (
        <div className="mt-3">
          <Aviso tono="info">{analisis.iaSinSaldo}</Aviso>
        </div>
      )}
      {!hayNombre && (
        <div className="mt-3">
          <Aviso tono="aviso">Hace falta una columna que vaya a «Nombre»: es lo único obligatorio.</Aviso>
        </div>
      )}
    </div>
  )
}

/** Una caja por causa: «En 19 productos: la imagen no se ha descargado…», y debajo, plegado, cuáles y con qué. */
const GrupoDeAvisosDeImportacion = ({ grupo, enlace }: { enlace: (id: number) => string; grupo: GrupoDeAvisos }) => {
  const productos = primeros(grupo.productos)
  const datos = primeros(grupo.datos)
  const cuantos = grupo.productos.length
  return (
    <li className="rounded-caja border border-aviso/30 bg-aviso-suave/40 px-3.5 py-3 text-[13px]">
      <p className="flex items-start gap-2">
        <AlertTriangle aria-hidden className="mt-0.5 size-4 shrink-0 text-aviso" />
        <span>
          <span className="font-semibold">{enCuantos(cuantos)}:</span> {enMinuscula(grupo.texto)}
        </span>
      </p>
      <details className="mt-1.5 pl-6">
        <summary className="cursor-pointer text-violeta hover:underline">
          Ver {cuantos === 1 ? 'cuál' : 'cuáles'}
          {grupo.datos.length > 0 ? ` y con qué (${grupo.datos.length})` : ''}
        </summary>
        <ul className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
          {productos.visibles.map((p) => (
            <li key={p.id}>
              <Link className="text-violeta hover:underline" to={enlace(p.id)}>
                {p.nombre}
              </Link>
            </li>
          ))}
          {productos.resto > 0 && <li className="text-tenue">y {productos.resto} más</li>}
        </ul>
        {grupo.datos.length > 0 && (
          <ul className="mt-2 flex flex-col gap-0.5 text-xs text-tinta-2">
            {datos.visibles.map((d) => (
              <li className="break-all" key={d} title={d}>
                {datoCorto(d)}
              </li>
            ))}
            {datos.resto > 0 && <li className="text-tenue">y {datos.resto} más</li>}
          </ul>
        )}
      </details>
    </li>
  )
}

/**
 * El informe de una importación. Los avisos van **agrupados por causa** —no repetidos
 * producto a producto— y la lista de productos solo dice cuántos tiene cada uno.
 */
export const ResumenDelInforme = ({
  informe,
  espacio,
  sitio,
  alPublicar,
  publicando,
  publicados,
}: {
  alPublicar?: (ids: number[]) => void
  espacio: number
  informe: Informe
  publicados?: number[]
  publicando?: boolean
  sitio: number
}) => {
  const sinAvisos = informe.creados.filter((c) => c.avisos.length === 0 && !(publicados ?? []).includes(c.id))
  const grupos = agruparAvisos(informe.creados)
  const enlace = (id: number) => `/e/${espacio}/s/${sitio}/productos/${id}`
  const reutilizadas = informe.totales.imagenesReutilizadas ?? 0
  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-caja border border-linea bg-papel p-4">
          <p className="text-2xl font-semibold">{informe.totales.creados}</p>
          <p className="text-[13px] text-tenue">{informe.totales.creados === 1 ? 'producto creado' : 'productos creados'} como borradores</p>
        </div>
        <div className="rounded-caja border border-linea bg-papel p-4">
          <p className="text-2xl font-semibold text-aviso">{informe.totales.conAvisos}</p>
          <p className="text-[13px] text-tenue">con avisos que conviene mirar</p>
        </div>
        <div className="rounded-caja border border-linea bg-papel p-4">
          <p className="text-2xl font-semibold text-error">{informe.totales.rechazados}</p>
          <p className="text-[13px] text-tenue">{informe.totales.rechazados === 1 ? 'fila rechazada' : 'filas rechazadas'}</p>
        </div>
      </div>
      {informe.formato === 'woocommerce' && <p className="text-[13px]">Formato reconocido: Excel de WooCommerce.</p>}
      {(informe.totales.categoriasCreadas > 0 || informe.totales.imagenesDescargadas > 0 || reutilizadas > 0) && (
        <p className="text-[13px] text-tenue">
          {informe.totales.categoriasCreadas > 0 && `${informe.totales.categoriasCreadas} ${informe.totales.categoriasCreadas === 1 ? 'categoría creada' : 'categorías creadas'}. `}
          {informe.totales.imagenesDescargadas > 0 && `${informe.totales.imagenesDescargadas} ${informe.totales.imagenesDescargadas === 1 ? 'imagen descargada' : 'imágenes descargadas'} a Medios. `}
          {reutilizadas > 0 && `${reutilizadas} ${reutilizadas === 1 ? 'imagen ya estaba' : 'imágenes ya estaban'} en Medios y se ${reutilizadas === 1 ? 'ha' : 'han'} reutilizado.`}
        </p>
      )}
      {(informe.notas ?? []).length > 0 && (
        <ul className="list-disc pl-5 text-[13px] text-tinta-2">
          {informe.notas!.map((nota) => (
            <li key={nota}>{nota}</li>
          ))}
        </ul>
      )}
      {grupos.length > 0 && (
        <section aria-label="Avisos de la importación">
          <h3 className="mb-2 text-sm font-semibold">Avisos que conviene mirar</h3>
          <ul className="flex flex-col gap-2">
            {grupos.map((g) => (
              <GrupoDeAvisosDeImportacion enlace={enlace} grupo={g} key={g.texto} />
            ))}
          </ul>
        </section>
      )}
      {informe.creados.length > 0 && (
        <ul aria-label="Productos creados" className="divide-y divide-linea rounded-caja border border-linea bg-papel">
          {informe.creados.map((c) => (
            <li className="flex items-center gap-3 px-4 py-2.5" key={c.id}>
              {c.avisos.length === 0 ? <CheckCircle2 aria-hidden className="size-4 shrink-0 text-ok" /> : <AlertTriangle aria-hidden className="size-4 shrink-0 text-aviso" />}
              <p className="min-w-0 flex-1 text-[13px]">
                <Link className="font-medium text-violeta hover:underline" to={enlace(c.id)}>
                  {c.nombre}
                </Link>
                {c.variantes > 0 && <span className="ml-2 text-xs text-tenue">{c.variantes} variantes</span>}
                {c.avisos.length > 0 && <span className="ml-2 text-xs text-aviso">{c.avisos.length === 1 ? '1 aviso' : `${c.avisos.length} avisos`}</span>}
                {(publicados ?? []).includes(c.id) && <span className="ml-2 text-xs text-ok">publicado</span>}
              </p>
            </li>
          ))}
        </ul>
      )}
      {informe.rechazados.length > 0 && (
        <ul aria-label="Filas rechazadas" className="divide-y divide-linea rounded-caja border border-error/30 bg-error-suave/40">
          {informe.rechazados.map((r) => (
            <li className="flex items-start gap-3 px-4 py-3 text-[13px]" key={r.filas.join(',')}>
              <XCircle aria-hidden className="mt-0.5 size-4 shrink-0 text-error" />
              <span>
                {r.filas.length === 1 ? `Fila ${r.filas[0]}` : `Filas ${r.filas.join(', ')}`}
                {r.nombre ? ` («${r.nombre}»)` : ''}: {r.motivo}
              </span>
            </li>
          ))}
        </ul>
      )}
      {alPublicar && sinAvisos.length > 0 && (
        <div className="flex flex-wrap items-center gap-3">
          <Boton cargando={publicando} onClick={() => alPublicar(sinAvisos.map((c) => c.id))} tipo="primario">
            Publicar {sinAvisos.length === 1 ? 'el producto' : `los ${sinAvisos.length} productos`} sin avisos
          </Boton>
          <span className="text-[13px] text-tenue">Los que tienen avisos se quedan en borrador para que los revises.</span>
        </div>
      )}
    </div>
  )
}

/** Cómo va: una frase y una barra, con las cuentas del núcleo. */
export const ProgresoDeImportacion = ({ trabajo }: { trabajo: Importacion }) => {
  const { productos } = trabajo.progreso
  const hecho = productos.total > 0 ? Math.round((productos.hechos / productos.total) * 100) : 0
  return (
    <div className="flex flex-col gap-2" role="status">
      <p className="flex items-center gap-2 text-sm">
        <Loader2 aria-hidden className="size-4 animate-spin text-violeta" />
        {textoDelProgreso(trabajo)}
      </p>
      <div aria-label="Productos importados" aria-valuemax={productos.total || 1} aria-valuemin={0} aria-valuenow={productos.hechos} className="h-2 overflow-hidden rounded-full bg-papel-3" role="progressbar">
        <div className="h-full rounded-full bg-violeta transition-[width] duration-500" style={{ width: `${hecho}%` }} />
      </div>
      <p className="text-xs text-tenue">
        Puedes salir de esta pantalla: la importación sigue sola y los productos van apareciendo en Productos como borradores. Para volver aquí, entra en Productos: arriba verás la última importación.
      </p>
    </div>
  )
}

/** «Se llegaron a crear 7 productos, que se quedan como borradores.» */
const loQueSeCreo = (creados: number) =>
  creados > 0 ? ` Se ${creados === 1 ? 'llegó a crear 1 producto, que se queda como borrador' : `llegaron a crear ${creados} productos, que se quedan como borradores`}.` : ''

/**
 * Sigue un trabajo de importación hasta que termina y enseña lo que haya: el progreso,
 * el fallo o el corte (con lo que llegó a crear y «Volver a intentarlo») o el informe,
 * con «publicar los que no tienen avisos». La usan Productos → Importar y el progreso
 * del alta.
 *
 * «Volver a intentarlo» va por el id: el núcleo ya tiene el fichero y el mapeo, así que
 * funciona aunque se haya recargado la página, y sigue desde el último producto guardado.
 */
export const SeguimientoDeImportacion = ({ espacio, sitio, id }: { espacio: number; id: number; sitio: number }) => {
  const clienteDeConsultas = useQueryClient()
  const { data: trabajo, error } = useImportacion(sitio, id)
  const [publicados, setPublicados] = useState<number[]>([])
  const [publicando, setPublicando] = useState(false)
  const [reintentando, setReintentando] = useState(false)
  const [mensaje, setMensaje] = useState<null | string>(null)

  const estado = trabajo?.estado
  useEffect(() => {
    if (!trabajo || (estado !== 'terminada' && estado !== 'fallida')) return
    // Los listados de productos y categorías tienen que enterarse de lo nuevo.
    void clienteDeConsultas.invalidateQueries({ queryKey: ['lista', 'productos'] })
    void clienteDeConsultas.invalidateQueries({ queryKey: ['lista', 'categorias'] })
    void clienteDeConsultas.invalidateQueries({ queryKey: ['importacion', sitio, 'ultima'] })
    // Solo al cambiar de estado: `trabajo` cambia en cada sondeo.
  }, [estado])

  const reintentar = async () => {
    setReintentando(true)
    setMensaje(null)
    try {
      const r = await importacion.reintentar(sitio, id)
      clienteDeConsultas.setQueryData(['importacion', sitio, id], r)
    } catch (e) {
      setMensaje((e as ErrorDeLaApi).message || 'No se ha podido volver a intentar. Prueba otra vez en un momento.')
    } finally {
      setReintentando(false)
    }
  }
  const botonDeReintentar = (texto: string) => (
    <Boton cargando={reintentando} icono={<RotateCcw />} onClick={() => void reintentar()} tipo="primario">
      {texto}
    </Boton>
  )

  if (error && (!trabajo || esRespuestaDefinitiva(error))) return <Aviso tono="error">{(error as ErrorDeLaApi).message || 'No se ha podido saber cómo va la importación.'}</Aviso>
  if (!trabajo) return <Cargando que="Mirando cómo va la importación" />
  // Un 5xx o la red mientras se sondea (un despliegue del núcleo): lo último que se sabe, y que se sigue preguntando.
  const sinConexion = error ? (
    <Aviso tono="aviso">Se ha perdido la conexión con el servidor; seguimos preguntando. La importación no depende de esta pantalla.</Aviso>
  ) : null
  if (trabajo.cortada) {
    return (
      <div className="flex flex-col gap-3">
        {sinConexion}
        {mensaje && <Aviso tono="error">{mensaje}</Aviso>}
        <Aviso tono="aviso">
          La importación se ha cortado: el servidor dejó de trabajar en ella (por ejemplo, al reiniciarse). Pulsa «Seguir importando» y continuará desde el último producto guardado, sin repetir los que ya estén.
          {loQueSeCreo(trabajo.creados)}
        </Aviso>
        <div>{botonDeReintentar('Seguir importando')}</div>
      </div>
    )
  }
  if (enMarcha(trabajo.estado)) {
    return (
      <div className="flex flex-col gap-3">
        {sinConexion}
        {mensaje && <Aviso tono="error">{mensaje}</Aviso>}
        <ProgresoDeImportacion trabajo={trabajo} />
      </div>
    )
  }
  if (trabajo.estado === 'fallida') {
    return (
      <div className="flex flex-col gap-3">
        {mensaje && <Aviso tono="error">{mensaje}</Aviso>}
        <Aviso tono="error">
          {trabajo.error ?? 'La importación no ha terminado.'}
          {loQueSeCreo(trabajo.creados)}
        </Aviso>
        {trabajo.reintentable && <div>{botonDeReintentar('Volver a intentarlo')}</div>}
      </div>
    )
  }
  if (!trabajo.informe) return <Aviso tono="error">La importación terminó pero no ha dejado informe. Mira los productos en Productos.</Aviso>

  const publicar = async (ids: number[]) => {
    setPublicando(true)
    setMensaje(null)
    try {
      const r = await importacion.publicar(sitio, ids)
      setPublicados((p) => [...p, ...r.publicados])
      if (r.fallidos.length > 0) setMensaje(`${r.fallidos.length} no se ${r.fallidos.length === 1 ? 'pudo' : 'pudieron'} publicar: ${r.fallidos.map((f) => f.motivo).join(' · ')}`)
      void clienteDeConsultas.invalidateQueries({ queryKey: ['lista', 'productos'] })
    } catch (e) {
      setMensaje((e as ErrorDeLaApi).message || 'No se han podido publicar. Vuelve a intentarlo.')
    } finally {
      setPublicando(false)
    }
  }
  return (
    <div className="flex flex-col gap-3">
      {mensaje && <Aviso tono="aviso">{mensaje}</Aviso>}
      <ResumenDelInforme alPublicar={(ids) => void publicar(ids)} espacio={espacio} informe={trabajo.informe} publicados={publicados} publicando={publicando} sitio={sitio} />
    </div>
  )
}

/** Cuánto tiempo sigue en Productos el aviso de una importación terminada. */
const AVISO_DE_TERMINADA_MS = 7 * 24 * 60 * 60 * 1000

/**
 * El aviso de Productos con la última importación de la web: la manera de volver a su
 * progreso o a su informe (con los avisos agrupados y «Publicar los que no tienen
 * avisos») después de salir de la pantalla. En marcha, cortada o fallida, siempre; una
 * terminada, durante una semana.
 */
export const AvisoDeUltimaImportacion = ({ base, sitio }: { base: string; sitio: number }) => {
  const { data } = useUltimaImportacion(sitio, true)
  const ultima = data?.importacion
  if (!ultima) return null
  const terminada = ultima.estado === 'terminada'
  if (terminada && (!ultima.terminadaEn || Date.now() - Date.parse(ultima.terminadaEn) > AVISO_DE_TERMINADA_MS)) return null
  const fichero = ultima.archivo ? `«${ultima.archivo}»` : 'del Excel'
  const totales = ultima.totales
  const texto = ultima.cortada
    ? `La importación ${fichero} se ha cortado. Puedes seguirla desde el último producto guardado.`
    : enMarcha(ultima.estado)
      ? `Hay una importación en marcha (${fichero}). ${textoDelProgreso(ultima)}`
      : ultima.estado === 'fallida'
        ? `La última importación (${fichero}) no terminó.`
        : `Última importación (${fichero}): ${totales?.creados ?? 0} ${totales?.creados === 1 ? 'producto creado' : 'productos creados'} como borradores${totales?.conAvisos ? `, ${totales.conAvisos} con avisos que conviene mirar` : ''}.`
  const enlace = ultima.cortada ? 'Ver y seguir' : enMarcha(ultima.estado) ? 'Ver cómo va' : ultima.estado === 'fallida' ? 'Ver qué pasó' : 'Ver el informe'
  return (
    <div className="mb-4">
      <Aviso tono={ultima.estado === 'fallida' || ultima.cortada ? 'aviso' : 'info'}>
        {texto}{' '}
        <Link className="font-medium text-violeta hover:underline" to={`${base}/importar?trabajo=${ultima.id}`}>
          {enlace}
        </Link>
      </Aviso>
    </div>
  )
}

export const Importar = ({ base, espacio, sitio }: { base: string; espacio: number; sitio: number }) => {
  const [buscar, setBuscar] = useSearchParams()
  const clienteDeConsultas = useQueryClient()
  const [archivo, setArchivo] = useState<File | null>(null)
  const [analisis, setAnalisis] = useState<Analisis | null>(null)
  const [mapeo, setMapeo] = useState<Mapeo[]>([])
  const [ocupado, setOcupado] = useState<null | string>(null)
  const [error, setError] = useState<null | string>(null)
  // El trabajo va en la dirección: al recargar, o al volver con «atrás», se sigue viendo cómo va.
  const idDelTrabajo = Number(buscar.get('trabajo')) || null
  const { data: trabajo } = useImportacion(sitio, idDelTrabajo)

  const fallo = (e: unknown) => setError((e as ErrorDeLaApi).message || 'Algo no ha ido bien. Vuelve a intentarlo.')
  const seguir = (id: null | number) => setBuscar(id === null ? {} : { trabajo: String(id) }, { replace: id === null })

  const analizar = async (f: File) => {
    setError(null)
    seguir(null)
    setOcupado('Leyendo el Excel…')
    try {
      const a = await importacion.analizar(f, { sitio })
      setArchivo(f)
      setAnalisis(a)
      setMapeo(a.mapeo)
    } catch (e) {
      fallo(e)
    } finally {
      setOcupado(null)
    }
  }

  const importar = async (o: { repetir?: boolean } = {}) => {
    if (!archivo) return
    setError(null)
    setOcupado('Poniendo la importación en marcha…')
    try {
      const r = await importacion.importar(sitio, archivo, mapeo, o)
      // Lo que ha respondido ya es el primer estado: no hace falta esperar al primer sondeo.
      clienteDeConsultas.setQueryData(['importacion', sitio, r.id], r)
      seguir(r.id)
    } catch (e) {
      fallo(e)
    } finally {
      setOcupado(null)
    }
  }

  const empezarDeNuevo = () => {
    setAnalisis(null)
    setArchivo(null)
    setError(null)
    seguir(null)
  }

  // «Volver a intentarlo» está dentro del seguimiento: va por el id, sin necesitar el fichero en el navegador.
  const acciones = trabajo && (!enMarcha(trabajo.estado) || trabajo.cortada) && (
    <div className="mt-5 flex flex-wrap gap-2 border-t border-linea pt-4">
      <Link to={base}>
        <Boton tipo={trabajo.estado === 'terminada' ? 'primario' : undefined}>Ver los productos</Boton>
      </Link>
      <Boton onClick={empezarDeNuevo}>Importar otro Excel</Boton>
    </div>
  )

  return (
    <>
      <Titulo
        accion={
          <a href={urlDeLaPlantilla(sitio)}>
            <Boton icono={<Download />}>Bajar la plantilla</Boton>
          </a>
        }
        descripcion="Sube un Excel con tus productos —la plantilla o el tuyo—, confirma qué columna va a qué campo, y se crean como borradores para que los revises."
        volver={
          <Link className="mb-2 inline-flex items-center gap-1 text-[13px] text-tenue hover:text-tinta" to={base}>
            <ArrowLeft className="size-3.5" /> Productos
          </Link>
        }
      >
        Importar productos
      </Titulo>

      <PasosDeImportacion actual={idDelTrabajo !== null ? (trabajo && !enMarcha(trabajo.estado) && !trabajo.cortada ? 3 : 2) : analisis && archivo ? 1 : 0} />

      {error && (
        <div className="mb-4">
          <Aviso tono="error">{error}</Aviso>
        </div>
      )}

      {idDelTrabajo !== null ? (
        <Tarjeta>
          <h2 className="font-display text-base font-semibold">
            {trabajo?.cortada ? 'La importación se ha cortado' : !trabajo || enMarcha(trabajo.estado) ? 'Importando' : trabajo.estado === 'fallida' ? 'La importación no ha terminado' : 'Importación terminada'}
            {trabajo?.archivo ? <span className="ml-2 text-sm font-normal text-tenue">{trabajo.archivo}</span> : null}
          </h2>
          {trabajo?.repetida && trabajo.estado === 'terminada' && (
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <Aviso tono="info">
                Este mismo fichero, con este mismo mapeo, ya se había importado en esta web: este es su informe y no se ha creado nada nuevo. Si quieres los productos otra vez (se crearán
                duplicados, con otro slug), impórtalo otra vez.
              </Aviso>
              {archivo && (
                <Boton cargando={ocupado !== null} icono={<Upload />} onClick={() => void importar({ repetir: true })}>
                  Importar otra vez
                </Boton>
              )}
            </div>
          )}
          <div className="mt-4">
            <SeguimientoDeImportacion espacio={espacio} id={idDelTrabajo} sitio={sitio} />
          </div>
          {acciones}
        </Tarjeta>
      ) : analisis && archivo ? (
        <Tarjeta>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <span className="grid size-10 shrink-0 place-items-center rounded-caja bg-violeta-suave text-violeta">
                <FileSpreadsheet className="size-5" />
              </span>
              <div className="min-w-0">
                <h2 className="truncate font-display text-base font-semibold" title={archivo.name}>
                  {archivo.name}
                </h2>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <Pastilla clave="neutra" punto={false}>
                    {analisis.totalDeFilas} {analisis.totalDeFilas === 1 ? 'fila' : 'filas'}
                  </Pastilla>
                  {analisis.formato === 'woocommerce' && (
                    <Pastilla clave="violeta" punto={false}>
                      Exportación de WooCommerce
                    </Pastilla>
                  )}
                </div>
              </div>
            </div>
            <button className="text-[13px] text-violeta hover:underline" onClick={() => setAnalisis(null)} type="button">
              Elegir otro fichero
            </button>
          </div>
          <p className="mt-4 mb-4 text-[13px] text-tenue">
            {analisis.formato === 'woocommerce'
              ? 'Hemos reconocido las columnas de WooCommerce y cada una ya va a su campo. Compruébalo y pulsa importar: las variaciones se juntan con su producto y las categorías se crean con su jerarquía.'
              : 'Comprueba a qué campo va cada columna. Las filas con el mismo nombre y distinta variante se agrupan en un producto.'}
          </p>
          <ComoQuedaran analisis={analisis} mapeo={mapeo} />
          <TablaDeMapeo analisis={analisis} cambiar={setMapeo} mapeo={mapeo} />
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Boton cargando={ocupado !== null} disabled={!mapeo.some((m) => m.campo === 'nombre')} icono={<Upload />} onClick={() => void importar()} tipo="primario">
              Importar {analisis.totalDeFilas} {analisis.totalDeFilas === 1 ? 'fila' : 'filas'} como borradores
            </Boton>
            {ocupado && (
              <span className="text-[13px] text-tenue" role="status">
                {ocupado}
              </span>
            )}
          </div>
        </Tarjeta>
      ) : (
        <Tarjeta>
          <SelectorDeArchivo alElegir={(f) => void analizar(f)} ocupado={ocupado !== null} />
          {ocupado && (
            <p className="mt-3 text-[13px] text-tenue" role="status">
              {ocupado}
            </p>
          )}
          <p className="mt-4 text-[13px] text-tenue">
            Las imágenes se ponen por el nombre del fichero si ya están en Medios, o por su dirección https:// (se descargan a tu biblioteca). Las categorías que no existan se crean.
          </p>
        </Tarjeta>
      )}
    </>
  )
}
